import { Alert, Button, Form, Modal, Radio, Select, Table, Tooltip } from "antd";
import { ArrowLeftOutlined, StopOutlined, WarningFilled } from '@ant-design/icons';
import { Core } from "../../components/infrastructure/core";
import { ICallOptions, IEntity, IResultSet } from "../../components/infrastructure/models";
import { EvaluationComponent, IEvaluationComponentFormProps, IEvaluationComponentProps, IEvaluationComponentStates, IEvaluationInitialData } from "./evaluationComponent";
import TextArea from "antd/lib/input/TextArea";
import { IDocumentType } from "../../baseInfo/documentType/interfaces";
import { IAttachment } from "../../baseInfo/attachment/interfaces";
import { AttachmentViewer } from "../../baseInfo/attachment/attachmentViewer";
import { enmDocumentCheckStates, enmEvaluationMethods, IDamageCase } from "../../baseInfo/damageCase/interfaces";
import React from "react";
import { IDamageReason } from "../../baseInfo/damageReason/interfaces";
import { AttachmentDownloader } from "../../baseInfo/attachment/attachmentDownloader";
import HelperPopover from "../../components/helperPopover";

type LayoutType = Parameters<typeof Form>[0]['layout'];

const { Option } = Select;

export const documentCheckStatesOptions = [
    { title: "رد خسارت", value: enmDocumentCheckStates.Reject },
    { title: "دارای نقص مدارک", value: enmDocumentCheckStates.Defective },
    { title: "تایید مدارک", value: enmDocumentCheckStates.Complete },
];

export interface IDefectiveDocument extends IEntity {
    DocumentCheckId: number;
    DocumentTypeId: number;
    DocumentType: IDocumentType;
}

export interface IDocumentCheck extends IEntity {
    DamageCaseEvaluatorId: number;
    DocumentCheckState: enmDocumentCheckStates;
    DamageReasonId?: number;
    DamageReason?: IDamageReason;
    Description?: string;
    DefectiveDocuments: IDefectiveDocument[];
    DefectiveDocumentTypes: number[];
    Attachments: IAttachment[];
    CheckInPersonOrOnline: boolean;
}

export interface IDocumentTypeAttachment {
    DocumentTypeId?: number;
    AttachmentId: number;
}

export interface IDocumentCheckStates extends IEvaluationComponentStates<IDocumentCheck> {
    documentTypes: IDocumentType[];
    damageReasons: IDamageReason[];
    documentTypeAttachments: IDocumentTypeAttachment[];
}

export interface IDocumentCheckProps extends IEvaluationComponentProps {

}

interface IDocumentCheckInitialData extends IEvaluationInitialData {
    DocumentTypes: IDocumentType[];
    DamageReasons: IDamageReason[];
}

export interface IDocumentCheckFormProps extends IEvaluationComponentFormProps<IDocumentCheck> {
    numberOfPreviousEvaluations?: number;
    documentTypes: IDocumentType[];
    damageReasons: IDamageReason[];
    damageCaseDocumentTypeAttachments: IAttachment[];
    documentTypeAttachments: IDocumentTypeAttachment[];
    onChangeDocumentCheckState?: (e: any) => void;
    onChangeDescription?: (value: any) => void;
    onChangeDefectiveDocumentTypes?: (selectedRowKeys: React.Key[]) => void;
    onChangeDamageReason?: (value: any) => void;
    onChangeDocumentTypeAttachment: (attachmentId: number, documentTypeId: number) => void;
    onChangeCheckInPersonOrOnline?: (e: any) => void;
}

const sampleDescriptions = [
    { type: enmDocumentCheckStates.Defective, message: "تصاویر ارسال شده فاقد وضوح کافی هستند" },
    { type: enmDocumentCheckStates.Defective, message: "مدارک اشتباهی ارسال شده اند" },
    { type: enmDocumentCheckStates.Reject, message: "خسارت تحت پوشش بیمه نامه نیست" },
    { type: enmDocumentCheckStates.Reject, message: "تاریخ خسارت خارج از پوشش بیمه نامه است" }
];

export const DocumentCheckForm = (props: IDocumentCheckFormProps) => {
    const formItemLayout = {
        labelCol: { xxl: 6, xl: 7, lg: 8, md: 9, sm: 10, xs: 10 },
        wrapperCol: { xxl: 12, xl: 17, lg: 16, md: 15, sm: 14, xs: 14 },
    };

    const [formLayout] = React.useState<LayoutType>('horizontal');

    const [form] = Form.useForm();

    React.useEffect(() => {
        let entity = props.currentEntity;
        if (!entity.DamageReasonId)
            entity.DamageReasonId = props.damageCase.DamageReasonId;
        if (props.damageCase.DamageReason?.includes('سرقت') === true)
            entity.CheckInPersonOrOnline = false;
        if (props.currentEntity.DefectiveDocuments)
            entity.DefectiveDocumentTypes = props.currentEntity.DefectiveDocuments.map(m => m.DocumentTypeId);
        form.setFieldsValue(entity);

    }, [props, form]);

    const numberOfPreviousEvaluations = props.numberOfPreviousEvaluations === undefined ? props.damageCase.NumberOfPreviousEvaluations : props.numberOfPreviousEvaluations;

    const documentTypes = props.documentTypes ??
        props.damageCaseDocumentTypeAttachments?.filter((a: any) => !!a.DamageCaseDocumentType.DocumentType)?.map((o: any) => o.DamageCaseDocumentType.DocumentType)
            .reduce((acc: IDocumentType[], current: IDocumentType) => {
                if (acc.map(a => a.Id).indexOf(current.Id) === -1)
                    acc.push(current);
                return acc;
            }, []);

    const documentTypesOptions = [(<Option key={"Empty"} value={null}>...</Option>), ...documentTypes.map(o => <Option key={o.Name} value={o.Id}>{o.Name}</Option>)];

    const { DefectiveDocuments } = props.currentEntity;

    const { currentEntity } = props;

    const { EvaluationMethod, PolicyEquipmentModel } = props.damageCase;

    if (DefectiveDocuments) {
        DefectiveDocuments.forEach(k => {
            if (documentTypes.map(d => d.Id).indexOf(k.DocumentTypeId) === -1) {
                documentTypes.push(k.DocumentType);
                documentTypesOptions.push(<Option key={k.DocumentType.Name} value={k.DocumentTypeId}>{k.DocumentType.Name}</Option>);
            }
        });
    }

    const damageReasonOptions = props.damageReasons
        ? props.damageReasons.map(o => <Option key={o.Name} value={o.Id}>{o.Name}</Option>)
        : [<Option key={props.damageCase.DamageReason} value={props.damageCase.DamageReasonId}>{props.damageCase.DamageReason}</Option>];

    const documentCheckState = form?.getFieldValue('DocumentCheckState');
    const descriptionPopoverContent =
        <div>
            {sampleDescriptions.filter(a => a.type === documentCheckState).map(a =>
                <p
                    key={a.message}
                    className="helperDescription"
                    onClick={() => {
                        const desc = form.getFieldValue('Description');
                        const value = (desc ? desc + '\n' : '') + a.message;
                        props.onChangeDescription && props.onChangeDescription(value);
                    }} >
                    {a.message}
                </p>)}
        </div>;


    const requireDescription = [enmDocumentCheckStates.Reject, enmDocumentCheckStates.Defective].indexOf(currentEntity.DocumentCheckState) > -1;

    const disableFormItems = props.disabled;// || (numberOfPreviousEvaluations === 1 && currentEntity.CheckInPersonOrOnline !== true);

    const { Policy } = PolicyEquipmentModel;
    const needPolicyUpdate = !Policy.CheckPolicyDate
        ? "بدلیل ناقص بودن اطلاعات بیمه نامه نمیتوانید پرونده جاری را بررسی نمایید، لطفاً اطلاعات بیمه نامه را بروزرسانی نمایید"
        : "";

    return (
        <Form
            scrollToFirstError
            form={form}
            {...formItemLayout}
            layout={formLayout}
            onFinish={async (values) => {
                props.onSubmit && await props.onSubmit(values);
            }}
            onReset={() => {
                props.onClose && props.onClose(this);
            }}
        >

            {!props.disabled &&
                <Alert
                    style={{ marginTop: 10, marginBottom: 20, textAlign: 'justify' }}
                    message={'ارزیاب گرامی، باتوجه به انتخاب شما به عنوان مرکز ارزیابی منتخب، خواهشمند است مدارک و مستندات پرونده خسارت را بررسی و نتیجه را ثبت نمایید'}
                    type="info"
                    showIcon
                />
            }

            {numberOfPreviousEvaluations >= 1 &&
                <Form.Item
                    label={'نیاز به ارزیابی مجدد؟'}
                    name={'CheckInPersonOrOnline'}
                    key={'CheckInPersonOrOnline'}
                    rules={[{ required: true, message: 'پاسخ اجباری است' }]}
                >
                    <Radio.Group key={'VideoConferencing'} onChange={props.onChangeCheckInPersonOrOnline} buttonStyle={"solid"} disabled={props.disabled}>
                        <Radio value={true}>بلی</Radio>
                        <Radio value={false}>خیر</Radio>
                    </Radio.Group>
                </Form.Item>
            }

            <div>

                <Form.Item
                    label={<span style={{ lineHeight: "normal" }}>نتیجه بررسی مدارک و مستندات؟</span>}
                    name={'DocumentCheckState'}
                    key={'DocumentCheckState'}
                    //rules={[{ required: numberOfPreviousEvaluations === 0 || (numberOfPreviousEvaluations >= 1 && currentEntity.CheckInPersonOrOnline === true), message: 'پاسخ اجباری است' }]}
                    //hidden={numberOfPreviousEvaluations >= 1 && currentEntity.CheckInPersonOrOnline !== true}
                >
                    <Radio.Group key={'DocumentCheckState'} onChange={props.onChangeDocumentCheckState} disabled={disableFormItems}>
                        {documentCheckStatesOptions.map(s =>
                            <Radio key={s.value} value={s.value}>{s.title}</Radio>
                        )}
                    </Radio.Group>
                </Form.Item>

                <Form.Item
                    label={'دلیل خسارت'}
                    name={'DamageReasonId'}
                    key={'DamageReasonId'}
                    rules={[{ required: true, message: 'دلیل خسارت الزامی است' }]}
                >
                    <Select
                        disabled={disableFormItems}
                        onChange={props.onChangeDamageReason}
                        key={'DamageReasonId'}
                    >
                        {damageReasonOptions}
                    </Select>
                </Form.Item>

                {currentEntity.DocumentCheckState === enmDocumentCheckStates.Complete && numberOfPreviousEvaluations === 0 && props.damageCase.DamageReason?.includes('سرقت') !== true &&
                    <Form.Item
                        label={EvaluationMethod === enmEvaluationMethods.VideoCall ? 'نیاز به ارزیابی آنلاین؟' : 'نیاز به ارزیابی حضوری؟'}
                        name={'CheckInPersonOrOnline'}
                        key={'CheckInPersonOrOnline'}
                        rules={[{ required: true, message: 'پاسخ اجباری است' }]}
                    >
                        <Radio.Group key={'VideoConferencing'} onChange={props.onChangeCheckInPersonOrOnline} buttonStyle={"solid"} disabled={disableFormItems}>
                            <Radio value={true}>بلی</Radio>
                            <Radio value={false}>خیر</Radio>
                        </Radio.Group>
                    </Form.Item>
                }

                <Form.Item
                    label={'توضیحات'}
                    name={'Description'}
                    key={'Description'}
                    style={{ display: requireDescription ? 'flex' : 'none' }}
                    rules={[{ required: requireDescription, message: 'توصیحات الزامی است' }]}
                    extra={disableFormItems ? <></> : <HelperPopover descriptionPopoverContent={descriptionPopoverContent} title="نمونه توضیحات" placement="right" style={{ marginRight: 1, verticalAlign: 'top', position: "absolute", top: 0, left: 0 }} />}
                >
                    <TextArea
                        rows={8}
                        onChange={e => { props.onChangeDescription && props.onChangeDescription(e.target.value); }}
                        disabled={disableFormItems}
                        style={{ width: disableFormItems ? "100%" : "calc(100% - 34px)" }}
                    />
                </Form.Item>

                <Form.Item
                    label={'مدارک دارای نقص'}
                    name={'DefectiveDocumentTypes'}
                    key={'DefectiveDocumentTypes'}
                    style={{ display: currentEntity.DocumentCheckState === enmDocumentCheckStates.Defective ? 'flex' : 'none' }}
                    rules={[{ required: (!currentEntity.DefectiveDocumentTypes || currentEntity.DefectiveDocumentTypes?.length === 0) && currentEntity.DocumentCheckState === enmDocumentCheckStates.Defective, message: 'مدارک دارای نقص را انتخاب نمایید' }]}
                >
                    <Table

                        loading={false}
                        scroll={{ x: "max-content" }}
                        rowKey="Id"
                        rowSelection={{
                            type: 'checkbox',
                            selectedRowKeys: currentEntity.DefectiveDocumentTypes,
                            onChange: props.onChangeDefectiveDocumentTypes,
                            columnTitle: 'دارای نقص؟',
                            selections: !props.disabled,
                            columnWidth: 100
                        }}
                        pagination={false}

                        onHeaderRow={() => {
                            return {
                                className: 'ant-table-thead-checkbox',
                            };
                        }}
                        dataSource={documentTypes}
                        columns={[
                            {
                                dataIndex: "Name",
                                title: "نوع مستند",
                                width: 200
                            }
                        ]}
                    />

                </Form.Item>

                <Form.Item
                    label={'مدارک و مستندات'}
                    name={'Attachments'}
                    key={'Attachments'}
                    hidden={!props.damageCaseDocumentTypeAttachments || props.damageCaseDocumentTypeAttachments.length === 0}
                >
                    {disableFormItems
                        ? <AttachmentDownloader key={'Attachments'} attachments={props.damageCaseDocumentTypeAttachments}
                            extraColumns={() => {
                                return [{
                                    title: 'نوع مستند',
                                    dataIndex: 'DocumentTypeId',
                                    key: 'DocumentTypeId',
                                    render: (_: any, record: IAttachment) => {
                                        return (
                                            <Select
                                                disabled={disableFormItems}
                                                defaultValue={(record as any)?.DamageCaseDocumentType?.DocumentTypeId ?? null}
                                            >
                                                {documentTypesOptions}
                                            </Select>
                                        );
                                    }
                                }];
                            }} />
                        : <AttachmentViewer key={'Attachments'} attachments={props.damageCaseDocumentTypeAttachments}
                            extraColumns={() => {
                                return [{

                                },
                                {
                                    title: 'نوع مستند',
                                    dataIndex: 'DocumentTypeId',
                                    key: 'DocumentTypeId',
                                    render: (_: any, record: IAttachment) => {
                                        const currentDocumentTypeAttachment = props.documentTypeAttachments.find(d => d.AttachmentId === record.Id);
                                        const documentTypeId = !!currentDocumentTypeAttachment ? currentDocumentTypeAttachment.DocumentTypeId : (record as any)?.DamageCaseDocumentType?.DocumentTypeId ?? null;
                                        return (
                                            <Select
                                                disabled={disableFormItems}
                                                onChange={(value) => props.onChangeDocumentTypeAttachment(record.Id, value)}
                                                defaultValue={documentTypeId}
                                            >
                                                {documentTypesOptions}
                                            </Select>
                                        );
                                    }
                                }];
                            }} />
                    }
                </Form.Item>

            </div>

            {props.disabled === true ? <></> :
                <Form.Item wrapperCol={{ dir: 'ltr' }} style={{ zIndex: 1000, borderTop: '1px solid', borderColor: '#e5e5e5', marginTop: 10 }}>
                    {needPolicyUpdate ?
                        <Tooltip title={needPolicyUpdate} color="orange">
                            <Button type="default" htmlType="button" loading={props.loading} style={{ color: "orangered", width: "100px", marginTop: 10, marginBottom: 10 }}>
                                <WarningFilled style={{ verticalAlign: "middle" }} />
                            </Button>
                        </Tooltip>
                        :
                        <Button type="primary" htmlType="submit" icon={<ArrowLeftOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10 }}>
                            بعدی
                        </Button>
                    }
                    <Button type="ghost" htmlType="reset" icon={<StopOutlined />} loading={props.loading} style={{ width: "100px", marginTop: 10, marginBottom: 10, marginLeft: 10, background: '#fff' }}>
                        لغو
                    </Button>

                </Form.Item>
            }

        </Form>
    );
}

export class DocumentCheck extends EvaluationComponent<IDocumentCheck, IDocumentCheckProps, IDocumentCheckStates>{
    onInitializing() {
        this.defaultEntity = {
            DamageTypeId: this.props.damageCase.DamageTypeId,
            DamageReasonId: this.props.damageCase.DamageReasonId
        } as any;

        this.defaultState = {
            documentTypes: [],
            damageCaseDocumentTypeAttachments: [],
            damageReasons: [],
            defectiveDocumentTypes: [],
            documentTypeAttachments: [],
            allDamageCases: []
        } as any;

        this.onInitialDataFetching = (params: any) => {
            params.equipmentId = this.props.damageCase.PolicyEquipmentModel.EquipmentId;
            params.damageCaseId = this.props.damageCase.Id;
            params.damageReasonId = this.props.damageCase.DamageReasonId;
            return params;
        };

        this.onInitialDataFetched = (result: IResultSet<any>) => {
            let documentCheckInitialData = result.Data as IDocumentCheckInitialData;

            this.setState({
                loading: false,
                evaluationSteps: documentCheckInitialData.EvaluationSteps,
                documentTypes: documentCheckInitialData.DocumentTypes,
                damageReasons: documentCheckInitialData.DamageReasons,
                damageCaseDocumentTypeAttachments: documentCheckInitialData.DamageCaseDocumentTypeAttachments,
                policyCert: documentCheckInitialData.PolicyCert,
                allDamageCases: documentCheckInitialData.AllDamageCases
            });
        };
    }

    onChangeDocumentCheckState = (e: any) => {
        let { currentEntity } = this.state;
        currentEntity.DocumentCheckState = e.target?.value;
        this.setState({ currentEntity });
    }
    onChangeDescription = (value: any) => {
        let { currentEntity } = this.state;
        currentEntity.Description = value;
        this.setState({ currentEntity });
    }
    onChangeDefectiveDocumentTypes = (selectedRowKeys: React.Key[]) => {
        this.setState({ currentEntity: { ...this.state.currentEntity, DefectiveDocumentTypes: selectedRowKeys as number[] } });
    }
    onChangeDamageReason = (value: any) => {
        let { currentEntity } = this.state;
        currentEntity.DamageReasonId = value;
        currentEntity.DamageReason = this.state.damageReasons.find(d => d.Id === currentEntity.DamageReasonId);
        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/getdocumenttypes",
            data: {
                equipmentId: this.props.damageCase.PolicyEquipmentModel.EquipmentId,
                damageReasonId: currentEntity.DamageReasonId
            },
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful && result.Data)
                    this.setState({ currentEntity, documentTypes: result.Data });
            }
        };
        this.callApi(fetchOpt);
    }
    onChangeDocumentTypeAttachment = (attachmentId: number, documentTypeId: number) => {
        let documentTypeAttachments = this.state.documentTypeAttachments;
        let documentTypeAttachment = documentTypeAttachments.find(d => d.AttachmentId === attachmentId);
        if (documentTypeAttachment)
            documentTypeAttachment.DocumentTypeId = documentTypeId;
        else
            documentTypeAttachments.push({ AttachmentId: attachmentId, DocumentTypeId: documentTypeId });
        this.setState({ documentTypeAttachments });
    }
    onChangeCheckInPersonOrOnline = (e: any) => {
        let { currentEntity } = this.state;
        currentEntity.CheckInPersonOrOnline = e.target.value;
        this.setState({ currentEntity });
    }
    onSubmit = async () => {
        let { currentEntity } = this.state;
        if (!currentEntity)
            return;

        let fetchOpt: ICallOptions = {
            url: "damagecaseevaluator/documentcheck",
            data: {
                evaluation: {
                    RowGuid: currentEntity.RowGuid,
                    DamageCaseEvaluatorId: currentEntity.DamageCaseEvaluatorId,
                    DocumentCheckState: this.props.damageCase.NumberOfPreviousEvaluations >= 1 && currentEntity.CheckInPersonOrOnline === false ? enmDocumentCheckStates.Complete : currentEntity.DocumentCheckState,
                    DamageReasonId: currentEntity.DamageReasonId,
                    Description: currentEntity.DocumentCheckState === enmDocumentCheckStates.Complete ? undefined : currentEntity.Description,
                    CheckInPersonOrOnline: currentEntity.DocumentCheckState === enmDocumentCheckStates.Complete ? currentEntity.CheckInPersonOrOnline : undefined
                },
                defectiveDocumentTypes: currentEntity.DocumentCheckState === enmDocumentCheckStates.Complete ? [] : (currentEntity.DefectiveDocumentTypes ?? []),
                documentTypeAttachments: this.state.documentTypeAttachments
            },
            onSuccess: (result: IResultSet<any>) => {
                if (result.ValidationState.Successful && result.Data) {
                    Core.successNotification('بررسی مدارک انجام شد');
                    if (currentEntity.DocumentCheckState === enmDocumentCheckStates.Complete) {
                        if (this.props.assignEvaluatorAccess) {
                            Modal.confirm({
                                title: 'تخصیص ارزیاب',
                                content: 'آیا مایلید کارشناس دیگری را جهت ادامه روند ارزیابی به این پرونده خسارت اختصاص دهید؟',
                                centered: true,
                                autoFocusButton: 'ok',
                                okText: 'بلی',
                                cancelText: 'خیر',
                                direction: 'rtl',
                                onOk: () => {
                                    this.props.nextStatus(result.Data as IDamageCase, true);
                                },
                                onCancel: () => {
                                    this.props.nextStatus(result.Data as IDamageCase);
                                }
                            });
                        }
                        else
                            this.props.nextStatus(result.Data as IDamageCase);
                    }
                    else
                        this.props.onClose && this.props.onClose(this);
                }
            }
        };
        return await this.callApi(fetchOpt);
    }
    renderBody() {
        return (
            <DocumentCheckForm
                key="DocumentCheckForm"
                loading={this.state.loading}
                currentEntity={this.state.currentEntity}
                damageCase={this.props.damageCase}
                onClose={this.props.onClose}
                onSubmit={this.onSubmit}
                damageCaseDocumentTypeAttachments={this.state.damageCaseDocumentTypeAttachments.flatMap(a => a)}
                documentTypes={this.state.documentTypes}
                documentTypeAttachments={this.state.documentTypeAttachments}
                onChangeDocumentCheckState={this.onChangeDocumentCheckState}
                onChangeCheckInPersonOrOnline={this.onChangeCheckInPersonOrOnline}
                onChangeDefectiveDocumentTypes={this.onChangeDefectiveDocumentTypes}
                onChangeDocumentTypeAttachment={this.onChangeDocumentTypeAttachment}
                onChangeDescription={this.onChangeDescription}
                onChangeDamageReason={this.onChangeDamageReason}
                damageReasons={this.state.damageReasons}
            />
        );
    }
}